/*
 * Раскладка редактора стилей (docs/release-2026-09-19.md §5.6). Чипы, сегмент, поля, плашки —
 * глобальные классы base.css; здесь только две панели, список файлов и поле во всю высоту.
 *
 * Поле текста — Geist, как всё в интерфейсе: файл стиля — это текст, который человек читает и
 * пишет глазами, а не данные (docs/ui-design.md §3, §5.2). Моноширинного здесь нет нигде.
 */

/* Страница — флекс-ребёнок каркаса Shell, поэтому прокрутку держит она, а не документ. */
.page {
  width: 100%;
  max-width: var(--surface-max);
  margin-inline: auto;
  flex: 1;
  min-width: 0;
  padding: var(--page-pad-y) var(--page-pad-x);
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.head {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Левая панель — 300px по спецификации, правая забирает остаток. minmax(0, 1fr): иначе
   длинная строка файла растягивает страницу, а не переносится. */
.panes {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.left {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
}

.right {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

/* Кнопки сегмента — настоящие кнопки, вид даёт глобальный `.seg-o` (как в панели превью). */
.segButton {
  font: inherit;
  border: none;
  cursor: pointer;
  background: none;
  flex: 1;
  justify-content: center;
}

/* Сегмент — fieldset: рамку, поля и min-width браузера снимаем, вид даёт глобальный `.seg`.
   Селектор составной, потому что base.css подключается ПОСЛЕ модулей. */
.left :global(.seg).seg {
  display: flex;
  margin: 0;
  min-width: 0;
}

.block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Группа версий — fieldset с legend «Версия»: подпись связана с чипами сама. */
.versions {
  border: none;
  margin: 0;
  padding: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.versions :global(.lbl) {
  margin-bottom: 0;
  padding: 0;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.versionActions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.files {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.group :global(.eyebrow) {
  padding: 0 10px 4px;
}

/* Строка файла — кнопка, обнулённая до строки бокового списка (docs/ui-design.md §7:
   14px, наведение `--violet-bg`, активный — приглушённая заливка). */
.file {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 7px 10px;
  border: none;
  border-left: 2px solid transparent;
  border-radius: 0 var(--r-ctl) var(--r-ctl) 0;
  background: none;
  font-family: var(--sans);
  text-align: left;
  cursor: pointer;
  min-width: 0;
}
.file:hover {
  background: var(--violet-bg);
}
.fileOn {
  background: var(--violet-bg);
  border-left-color: var(--violet);
}

.fileName {
  font-size: 14px;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fileOn .fileName {
  font-weight: 600;
}

.fileMeta {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.filePath {
  font-size: 10.5px;
  color: var(--muted);
  letter-spacing: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

/* «общий» — файл обоих сервисов: его правка меняет и писателя, и мониторинг. */
.badge {
  flex: none;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--accent-ink);
  background: var(--violet-bg);
  border: 1px solid var(--violet-bg-2);
  border-radius: var(--r-chip);
  padding: 1px 7px;
}

/* Точка «изменён» — отличие от базовой версии, а не несохранённая правка. */
.dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--violet);
}

/* Полоса релиза: вид — глобальный `.notice`, здесь только ряд «состояние + единственный путь
   его поправить — копия». */
.banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
}
.banner span {
  flex: 1;
  min-width: 200px;
}

.fileHead {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.fileHeadMeta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

/* Поле во всю высоту окна: файл стиля бывает на десятки экранов, и щель в двадцать строк
   превращает правку в прокрутку. Geist 14/1.55 — текст, а не код. */
.area {
  width: 100%;
  min-height: max(420px, calc(100dvh - 300px));
  resize: vertical;
  font-family: var(--sans);
  font-size: 14px;
  line-height: 1.55;
  tab-size: 2;
}
.area[readonly] {
  background: var(--bg-alt);
  color: var(--ink-2);
}

.fileBar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* Счётчик — данные: табличные цифры, чтобы строка не дёргалась при наборе. */
.counter {
  margin-left: auto;
  font-size: 12.5px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}

/* Плашка с действием («Повторить», «Восстановить / Отбросить», «Перечитать файл»): текст и
   кнопки в одну строку, на узком — переносом. Вид — глобальный `.val`; селектор составной
   (0,2,0): base.css подключается ПОСЛЕ модулей, и его `.val { align-items: flex-start }` при
   равной специфичности перебил бы. */
:global(.val).notice {
  align-items: center;
  flex-wrap: wrap;
}

.grow {
  flex: 1;
  min-width: 0;
}

/* Текст отказа проверки — это `KnowledgeError` загрузчика, и переводы строк в нём значимы. */
.preLine {
  white-space: pre-line;
}

.lines {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* tablet (1024): панели встают одна под другой — файл под списком. */
@media (max-width: 1024px) {
  .panes {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* phone (640): поле не требует экрана целиком — под ним ещё кнопки сохранения. */
@media (max-width: 640px) {
  .area {
    min-height: 60vh;
  }
}
